import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

import { DATE_FORMATS_FNS, canShowCustomerWaitingCarInShopNotification, showCustomerWaitingCarInShopNotification, showNotification } from "components";
import { typedQueryClient, userKey } from "hooks";
import { STATUS_IDENTIFIERS } from "models";
import { useNotificationsListQuery } from "modules/AccountSettings/hooks";
import { AccountSettingsQueryKeys } from "modules/AccountSettings/queryKeys";
import { PATHS } from "router/paths";
import { formatDate } from "utils/dateHelperFunctions";

import type { TFunction } from "i18next";
import type { Appointment, User } from "models";

export enum NOTIFICATION_ENTITIES {
  Appointment = 1,
  StatusIdentifier
}

type NotifyAppointmentUpdatedParams = {
  oldAppointment: Appointment;
  newAppointment: Appointment;
  userId?: number;
};

type NotifyAppointmentAddedParams = {
  appointment: Appointment;
  userId?: number;
};

const getCarShopStatus = (oldAppointment: Appointment, newAppointment: Appointment, t: TFunction) => {
  if (oldAppointment.car_out_of_shop && newAppointment.car_out_of_shop === null && newAppointment.car_in_shop) return t("v8_car_in_shop", "Car In Shop");
  if (oldAppointment.car_out_of_shop === null && newAppointment.car_out_of_shop !== null) return t("v8_car_out_of_shop", "Car Out of Shop");

  return "";
};

const hasNotificationSubscription = (id: number, user: User) => user.notification_element_ids?.includes(id);

const getSubscribedNotifications = (user: User) => {
  const notificationsList = typedQueryClient.getQueryData(AccountSettingsQueryKeys.notificationsList) ?? [];

  return {
    subscribedStatusNotifications: notificationsList.filter(
      n => n.notification_entity_id === NOTIFICATION_ENTITIES.StatusIdentifier && hasNotificationSubscription(n.notification_element_id, user)
    ),
    subscribedAppointmentNotifications: notificationsList.filter(
      n => n.notification_entity_id === NOTIFICATION_ENTITIES.Appointment && hasNotificationSubscription(n.notification_element_id, user)
    )
  };
};

export const useAppointmentNotifications = () => {
  const { t } = useTranslation();
  useNotificationsListQuery();

  const notifyAppointmentAdded = useCallback(
    ({ appointment, userId }: NotifyAppointmentAddedParams) => {
      const user = typedQueryClient.getQueryData(userKey);
      if (!user || user.id === userId || user.notification_element_ids?.length === 0) return;

      const { subscribedStatusNotifications } = getSubscribedNotifications(user);
      const matchingStatusNotification = subscribedStatusNotifications.find(({ field }) => `${appointment.appointment_status_identifier}` === field?.value);

      if (matchingStatusNotification)
        showNotification({
          id: `${uuidv4()} - ${appointment.id}`,
          time: formatDate(new Date(), DATE_FORMATS_FNS.hoursMinutes),
          type: "NewAppointmentNotification",
          title: `#${appointment.wo_nr}`,
          description: t("v8_new_appointment_has_been_added", "New appointment has been added"),
          path: `${window.location.origin}${PATHS.APPOINTMENTS}/${appointment.id}`,
          icon: matchingStatusNotification.field?.icon ?? "car garage"
        });
    },
    [t]
  );

  const notifyAppointmentUpdated = useCallback(
    ({ oldAppointment, newAppointment, userId }: NotifyAppointmentUpdatedParams) => {
      const user = typedQueryClient.getQueryData(userKey);
      if (!user || user.id === userId || user.notification_element_ids?.length === 0) return;

      const shownStatuses = new Set<STATUS_IDENTIFIERS>();

      const { subscribedStatusNotifications, subscribedAppointmentNotifications } = getSubscribedNotifications(user);
      const appointmentFieldsSet = new Set(subscribedAppointmentNotifications.flatMap(({ field }) => (field?.value ? [field.value] : [])));
      const isAppointmentKey = (key: string): key is keyof Appointment => appointmentFieldsSet.has(key);

      const matchingStatusNotification = subscribedStatusNotifications.find(({ field }) => `${newAppointment.appointment_status_identifier}` === field?.value);
      if (
        matchingStatusNotification &&
        oldAppointment.appointment_status_identifier !== newAppointment.appointment_status_identifier &&
        newAppointment.appointment_status_identifier !== STATUS_IDENTIFIERS.NewCarStatus
      ) {
        const isCarInShopStatus = `${STATUS_IDENTIFIERS.CarInShop}` === matchingStatusNotification.field?.value;
        if (isCarInShopStatus) shownStatuses.add(STATUS_IDENTIFIERS.CarInShop);

        if (isCarInShopStatus && canShowCustomerWaitingCarInShopNotification({ oldAppointment, newAppointment })) {
          showCustomerWaitingCarInShopNotification({ appointment: newAppointment, t });
        } else
          showNotification({
            id: `${uuidv4()} - ${newAppointment.id}`,
            time: formatDate(new Date(), DATE_FORMATS_FNS.hoursMinutes),
            title: `#${newAppointment.wo_nr}`,
            description: `${t("v8_status_updated_to", "Status Updated to")} ${t(`v8_${matchingStatusNotification.field?.name}`) || matchingStatusNotification.field?.name || ""} `,
            path: `/appointments/${newAppointment.id}`,
            appointment: newAppointment,
            type: "AppointmentUpdateNotification",
            icon: matchingStatusNotification.field?.icon || ""
          });
      }

      const carOutOfShopNotification = subscribedStatusNotifications.find(({ field }) => `${STATUS_IDENTIFIERS.CarOutOfShop}` === field?.value);
      if (carOutOfShopNotification && !oldAppointment.car_out_of_shop && newAppointment.car_out_of_shop) {
        shownStatuses.add(STATUS_IDENTIFIERS.CarOutOfShop);
        showNotification({
          id: `${uuidv4()} - ${newAppointment.id}`,
          time: formatDate(new Date(), DATE_FORMATS_FNS.hoursMinutes),
          title: `#${newAppointment.wo_nr}`,
          description: `${t("v8_status_updated_to", "Status Updated to")} ${t(`v8_${carOutOfShopNotification.field?.name}`) || (carOutOfShopNotification.field?.name ?? "")} `,
          path: `/appointments/${newAppointment.id}`,
          appointment: newAppointment,
          type: "AppointmentUpdateNotification",
          icon: carOutOfShopNotification.field?.icon ?? ""
        });
      }

      subscribedAppointmentNotifications.forEach(notification => {
        const notificationField = notification.field?.value;
        if (!notificationField || !isAppointmentKey(notificationField)) return;

        if (oldAppointment[notificationField] !== newAppointment[notificationField]) {
          const notificationName = notification.field?.name;

          if (notificationField === "car_in_shop" && appointmentFieldsSet.has("car_out_of_shop")) return;
          if (notificationField === "car_out_of_shop" || notificationField === "car_in_shop") {
            const carShopStatus = getCarShopStatus(oldAppointment, newAppointment, t);
            if (shownStatuses.has(STATUS_IDENTIFIERS.CarInShop) || shownStatuses.has(STATUS_IDENTIFIERS.CarOutOfShop) || !carShopStatus) return;

            showNotification({
              id: `${uuidv4()} - ${newAppointment.id}`,
              time: formatDate(new Date(), DATE_FORMATS_FNS.hoursMinutes),
              title: `#${newAppointment.wo_nr}`,
              description: `${t("v8_appointment_updated_to", "Appointment updated to")} ${carShopStatus}`,
              path: `/appointments/${newAppointment.id}`,
              type: "AppointmentUpdateNotification",
              icon: notification.field?.icon || ""
            });
            return;
          }

          if (notificationName && newAppointment[notificationField]) {
            showNotification({
              id: `${uuidv4()} - ${newAppointment.id}`,
              time: formatDate(new Date(), DATE_FORMATS_FNS.hoursMinutes),
              title: `#${newAppointment.wo_nr}`,
              description: `${t("v8_appointment_updated_to", "Appointment updated to")} ${t(`v8_${notificationName}`, notificationName) || ""}`,
              path: `/appointments/${newAppointment.id}`,
              type: "AppointmentUpdateNotification",
              icon: notification.field?.icon || ""
            });
          }
        }
      });
    },
    [t]
  );

  return { notifyAppointmentUpdated, notifyAppointmentAdded };
};
